import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { track } from '@/lib/analytics';
import {
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiBarChart2,
} from 'react-icons/fi';
import { Card, Button } from '@/components';
import { ExchangeStatusBadge } from '@/components/ExchangeStatusBadge';
import { apiJson, ApiError } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import { formatDateLocal } from '@/lib/utils';
import { getExchangeLogs, type ExchangeLog } from '@/lib/exchange-api';
import type {
  CalendarResponse,
  CalendarDay,
  CustodyAssignment,
  EventOccurrence,
  UserResponse,
  HouseholdResponse,
} from '@/lib/types';
import { SCHEDULE_TYPE_NAMES } from '@/lib/types';

function getActualCustodyHolderStatic(
  assignment: CustodyAssignment,
  currentUserId: number,
  coparentUserId: number | undefined
): number {
  if (
    assignment.exception_status === 'PENDING' &&
    assignment.exception_type !== 'TIME_CHANGE'
  ) {
    if (assignment.assigned_user_id === currentUserId) {
      return coparentUserId ?? assignment.assigned_user_id;
    }
    if (coparentUserId && assignment.assigned_user_id === coparentUserId) {
      return currentUserId;
    }
  }
  return assignment.assigned_user_id;
}

function getExchangeTransitionMeta(
  assignment: CustodyAssignment,
  selectedDate: string,
  calendarData: CalendarDay[],
  currentUserId: number,
  coparentUserId: number | undefined
): { exchangeType: 'PICKUP' | 'DROPOFF'; scheduledTime: string } | null {
  const actualHolder = getActualCustodyHolderStatic(
    assignment,
    currentUserId,
    coparentUserId
  );
  const youHaveCustody = actualHolder === currentUserId;

  const prevDate = new Date(selectedDate + 'T12:00:00');
  prevDate.setDate(prevDate.getDate() - 1);
  const prevStr = formatDateLocal(prevDate);
  const prevDay = calendarData.find((d) => d.date === prevStr);
  const prevA = prevDay?.assignments.find(
    (a) => a.beneficiary_id === assignment.beneficiary_id
  );

  if (prevA) {
    const prevHolder = getActualCustodyHolderStatic(
      prevA,
      currentUserId,
      coparentUserId
    );
    if (prevHolder !== actualHolder) {
      const exchangeType = youHaveCustody ? 'PICKUP' : 'DROPOFF';
      const scheduledTime =
        exchangeType === 'PICKUP'
          ? assignment.pickup_time ?? ''
          : assignment.dropoff_time ?? '';
      return { exchangeType, scheduledTime };
    }
  }

  const nextDate = new Date(selectedDate + 'T12:00:00');
  nextDate.setDate(nextDate.getDate() + 1);
  const nextStr = formatDateLocal(nextDate);
  const nextDay = calendarData.find((d) => d.date === nextStr);
  const nextA = nextDay?.assignments.find(
    (a) => a.beneficiary_id === assignment.beneficiary_id
  );

  if (nextA) {
    const nextHolder = getActualCustodyHolderStatic(
      nextA,
      currentUserId,
      coparentUserId
    );
    if (nextHolder !== actualHolder) {
      const exchangeType = youHaveCustody ? 'DROPOFF' : 'PICKUP';
      const scheduledTime =
        exchangeType === 'PICKUP'
          ? assignment.pickup_time ?? ''
          : assignment.dropoff_time ?? '';
      return { exchangeType, scheduledTime };
    }
  }

  return null;
}

// Colors for custody display
const CUSTODY_COLORS = {
  youHaveKids: '#92CDD5', // Light teal - you have at least one child
  youHaveKidsToday: '#004F5D', // Dark teal for today
  noKids: '#F1E5D8', // Light beige - co-parent has all children
  exception: '#F59E0B', // Amber - for pending exceptions
};

// Helper to get start and end of month
function getMonthRange(date: Date): { startDate: string; endDate: string } {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  return {
    startDate: formatDateLocal(firstDay),
    endDate: formatDateLocal(lastDay),
  };
}

// Get days in month for calendar grid
function getCalendarDays(date: Date): (Date | null)[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay(); // 0 = Sunday

  const days: (Date | null)[] = [];

  // Add empty slots for days before the first of the month
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }

  // Add the days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(new Date(year, month, day));
  }

  return days;
}

function CalendarPage() {
  const { getToken, isSignedIn } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [fabOpen, setFabOpen] = useState(false);

  // Track screen view
  useEffect(() => {
    track('Calendar Screen');
  }, []);

  const todayString = formatDateLocal(new Date());

  const { currentHouseholdId } = useHouseholds();

  // Get current user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  // Get household for member info
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  // Get calendar data for the current month
  const { startDate, endDate } = useMemo(() => getMonthRange(currentDate), [currentDate]);

  const calendarQuery = useQuery({
    queryKey: ['calendar', currentHouseholdId, startDate, endDate],
    queryFn: async () => {
      try {
        return await apiJson<CalendarResponse>(
          getToken,
          `/v1/calendar?start_date=${startDate}&end_date=${endDate}`
        );
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
          return { calendar: [] } as CalendarResponse;
        }
        throw err;
      }
    },
  });

  const exchangeLogsQuery = useQuery({
    queryKey: ['exchange-logs', currentHouseholdId, startDate, endDate],
    queryFn: () =>
      getExchangeLogs(getToken, { start_date: startDate, end_date: endDate }),
    enabled: !!isSignedIn,
  });

  const exchangeLogsByKey = useMemo(() => {
    const m = new Map<string, ExchangeLog>();
    for (const log of exchangeLogsQuery.data?.exchange_logs ?? []) {
      m.set(`${log.scheduled_date}|${log.exchange_type}`, log);
    }
    return m;
  }, [exchangeLogsQuery.data]);

  const currentUser = userQuery.data;
  const households = householdsQuery.data ?? [];
  const firstHousehold =
    households.find((h) => h.id === currentHouseholdId) ?? households[0];
  const calendarData = useMemo(() => calendarQuery.data?.calendar ?? [], [calendarQuery.data?.calendar]);

  // Get co-parent info
  const coparent = useMemo(() => {
    if (!firstHousehold || !currentUser) return null;
    return firstHousehold.members.find((m) => m.user_id !== currentUser.id) ?? null;
  }, [firstHousehold, currentUser]);

  // Build calendar data map
  const calendarDataMap = useMemo(() => {
    const map: Record<string, { assignments: CustodyAssignment[]; youHaveKids: boolean; hasException: boolean; hasPendingException: boolean }> = {};
    
    calendarData.forEach((day) => {
      if (day.assignments.length === 0) return;

      // Check if current user has ANY of the children that day
      const youHaveAnyChild = day.assignments.some((a) => {
        if (
          a.exception_status === 'PENDING' &&
          a.exception_type !== 'TIME_CHANGE'
        ) {
          if (a.assigned_user_id === currentUser?.id) {
            return false;
          }
          return true;
        }
        return a.assigned_user_id === currentUser?.id;
      });

      const hasException = day.assignments.some((a) => a.is_exception);
      const hasPendingException = day.assignments.some((a) => a.exception_status === 'PENDING');

      map[day.date] = {
        assignments: day.assignments,
        youHaveKids: youHaveAnyChild,
        hasException,
        hasPendingException,
      };
    });

    return map;
  }, [calendarData, currentUser]);

  // Get assignments for selected date
  const selectedDayAssignments = useMemo(() => {
    if (!selectedDate) return [];
    return calendarDataMap[selectedDate]?.assignments ?? [];
  }, [selectedDate, calendarDataMap]);

  const selectedDayEvents = useMemo(() => {
    if (!selectedDate) return [] as EventOccurrence[];
    return calendarData.find((day) => day.date === selectedDate)?.events ?? [];
  }, [selectedDate, calendarData]);

  // Extract unique custody schedules for the carousel
  const uniqueSchedules = useMemo(() => {
    const scheduleMap = new Map<
      number,
      { id: number; type: string; beneficiaryName: string }
    >();

    calendarData.forEach((day) => {
      day.assignments.forEach((assignment) => {
        if (assignment.schedule_id && !scheduleMap.has(assignment.schedule_id)) {
          scheduleMap.set(assignment.schedule_id, {
            id: assignment.schedule_id,
            type: assignment.schedule_type ?? 'Unknown',
            beneficiaryName: assignment.beneficiary_name,
          });
        }
      });
    });

    return Array.from(scheduleMap.values());
  }, [calendarData]);

  const calendarDays = useMemo(() => getCalendarDays(currentDate), [currentDate]);
  const hasSchedules = calendarData.some((day) => day.assignments.length > 0);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const getParentName = (userId: number): string => {
    if (userId === currentUser?.id) return 'You';
    if (coparent && userId === coparent.user_id) {
      return coparent.first_name ?? 'Co-parent';
    }
    return 'Parent';
  };

  const getActualCustodyHolder = (assignment: CustodyAssignment): number => {
    if (
      assignment.exception_status === 'PENDING' &&
      assignment.exception_type !== 'TIME_CHANGE'
    ) {
      if (assignment.assigned_user_id === currentUser?.id) {
        return coparent?.user_id ?? assignment.assigned_user_id;
      }
      if (coparent && assignment.assigned_user_id === coparent.user_id) {
        return currentUser?.id ?? assignment.assigned_user_id;
      }
    }
    return assignment.assigned_user_id;
  };

  // Format time string (HH:MM:SS) for display
  const formatTimeDisplay = (timeStr: string | null): string => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Mobile Header */}
      <header className='lg:hidden bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4'>
          <h1
            className='text-2xl font-light text-primary text-center'
            style={{
              fontFamily: "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Calendar
          </h1>
        </div>
      </header>

      {/* Desktop Header */}
      <header className='hidden lg:block bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-8 py-4'>
          <div className='flex items-center justify-between'>
            <h1
              className='text-xl font-semibold text-gray-900'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Calendar
            </h1>
          </div>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6 space-y-6'>
        {/* Legend */}
        {hasSchedules && (
          <div className='flex flex-wrap justify-center gap-4 text-sm'>
            <div className='flex items-center gap-2'>
              <div
                className='w-4 h-4 rounded'
                style={{ backgroundColor: CUSTODY_COLORS.youHaveKids }}
              />
              <span className='text-gray-600'>You have kids</span>
            </div>
            <div className='flex items-center gap-2'>
              <div
                className='w-4 h-4 rounded'
                style={{ backgroundColor: CUSTODY_COLORS.noKids }}
              />
              <span className='text-gray-600'>{coparent?.first_name ?? 'Co-parent'} has kids</span>
            </div>
            <div className='flex items-center gap-2'>
              <div
                className='w-2 h-2 rounded-full'
                style={{ backgroundColor: CUSTODY_COLORS.exception }}
              />
              <span className='text-gray-600'>Pending</span>
            </div>
          </div>
        )}

        {hasSchedules && (
          <div className='flex flex-wrap justify-center gap-2'>
            <Link
              to='/app/calendar/check-ins'
              onClick={() => track('GPS Check-ins Chip Tapped')}
              className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-semibold text-primary hover:border-primary/40 transition-colors'
            >
              <FiMapPin className='w-4 h-4' />
              GPS check-ins
            </Link>
            <Link
              to='/app/calendar/compliance'
              onClick={() => track('Exchange History Tapped')}
              className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-semibold text-primary hover:border-primary/40 transition-colors'
            >
              <FiBarChart2 className='w-4 h-4' />
              Exchange history
            </Link>
          </div>
        )}

        {/* Month Navigation */}
        <Card>
          <div className='flex items-center justify-between mb-4'>
            <button
              onClick={handlePrevMonth}
              className='p-2 hover:bg-gray-100 rounded-full transition-colors'
            >
              <FiChevronLeft className='w-5 h-5 text-primary' />
            </button>
            <h2
              className='text-lg font-semibold text-primary'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              {monthYear}
            </h2>
            <button
              onClick={handleNextMonth}
              className='p-2 hover:bg-gray-100 rounded-full transition-colors'
            >
              <FiChevronRight className='w-5 h-5 text-primary' />
            </button>
          </div>

          {/* Week Headers */}
          <div className='grid grid-cols-7 gap-1 mb-2'>
            {weekDays.map((day) => (
              <div key={day} className='text-center text-xs font-semibold text-gray-500 py-2'>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className='grid grid-cols-7 gap-1'>
            {calendarDays.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className='aspect-square' />;
              }

              const dateStr = formatDateLocal(day);
              const dayData = calendarDataMap[dateStr];
              const isToday = dateStr === todayString;
              const isSelected = dateStr === selectedDate;
              const hasData = !!dayData;

              let bgColor = 'transparent';
              let textColor = 'text-gray-900';

              if (hasData) {
                if (dayData.youHaveKids) {
                  bgColor = isToday ? CUSTODY_COLORS.youHaveKidsToday : CUSTODY_COLORS.youHaveKids;
                  textColor = isToday ? 'text-white' : 'text-gray-900';
                } else {
                  bgColor = CUSTODY_COLORS.noKids;
                }
              }

              return (
                <button
                  key={dateStr}
                  onClick={() => setSelectedDate(dateStr)}
                  className={`
                    aspect-square rounded-lg flex flex-col items-center justify-center relative
                    transition-all hover:ring-2 hover:ring-primary/30
                    ${isSelected ? 'ring-2 ring-gray-900' : ''}
                    ${!hasData && isToday ? 'ring-2 ring-primary' : ''}
                  `}
                  style={{ backgroundColor: bgColor }}
                >
                  <span className={`text-sm font-semibold ${textColor}`}>{day.getDate()}</span>
                  {dayData?.hasPendingException && (
                    <div
                      className='absolute bottom-1 w-1.5 h-1.5 rounded-full'
                      style={{ backgroundColor: CUSTODY_COLORS.exception }}
                    />
                  )}
                  {dayData?.hasException && !dayData?.hasPendingException && (
                    <div className='absolute bottom-1 w-1.5 h-1.5 rounded-full bg-primary' />
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Custody Schedules Carousel */}
        {uniqueSchedules.length > 0 && (
          <div>
            <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3'>
              Schedules
            </p>
            <div className='flex gap-3 overflow-x-auto pb-1 -mx-6 px-6 lg:-mx-8 lg:px-8'>
              {uniqueSchedules.map((schedule) => (
                <Link
                  key={schedule.id}
                  to='/app/calendar/$scheduleId'
                  params={{ scheduleId: String(schedule.id) }}
                  className='flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-3 py-2.5 hover:border-primary/30 hover:shadow-sm transition-all flex-shrink-0'
                >
                  <div className='w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center'>
                    <FiCalendar className='w-4 h-4 text-primary' />
                  </div>
                  <div>
                    <p className='font-bold text-gray-900 text-sm'>{schedule.beneficiaryName}</p>
                    <p className='text-xs font-semibold text-primary'>
                      {SCHEDULE_TYPE_NAMES[schedule.type] ?? schedule.type}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Selected Day Details */}
        {selectedDate && selectedDayAssignments.length > 0 && (
          <Card>
            <h3
              className='font-bold text-gray-900 mb-4'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </h3>
            <div className='space-y-3'>
              {selectedDayAssignments.map((assignment) => {
                const actualCustodyHolder = getActualCustodyHolder(assignment);
                const isCurrentUserCustody = actualCustodyHolder === currentUser?.id;
                
                // Determine transition labels with perspective
                let pickupLabel = '';
                let dropoffLabel = '';
                
                if (assignment.is_first_day_of_segment && assignment.pickup_time) {
                  const timeStr = formatTimeDisplay(assignment.pickup_time);
                  pickupLabel = isCurrentUserCustody 
                    ? `Pick up at ${timeStr}` 
                    : `Drop off at ${timeStr}`;
                }
                
                if (assignment.is_last_day_of_segment && assignment.dropoff_time) {
                  const timeStr = formatTimeDisplay(assignment.dropoff_time);
                  dropoffLabel = isCurrentUserCustody 
                    ? `Drop off at ${timeStr}` 
                    : `Pick up at ${timeStr}`;
                }
                
                return (
                  <div
                    key={assignment.beneficiary_id}
                    className='flex items-center justify-between p-3 bg-gray-50 rounded-xl'
                  >
                    <div>
                      <p className='font-semibold text-gray-900'>{assignment.beneficiary_name}</p>
                      {assignment.custody_start_time && assignment.custody_end_time && (
                        <div className='flex items-center gap-1 mt-1'>
                          <FiClock className='w-3 h-3 text-gray-500' />
                          <span className='text-xs text-gray-600'>
                            {formatTimeDisplay(assignment.custody_start_time)} –{' '}
                            {formatTimeDisplay(assignment.custody_end_time)}
                          </span>
                        </div>
                      )}
                      {assignment.schedule_type && (
                        <div className='flex items-center gap-1 mt-1'>
                          <FiCalendar className='w-3 h-3 text-primary' />
                          <span className='text-xs font-semibold text-primary'>
                            {SCHEDULE_TYPE_NAMES[assignment.schedule_type] ?? assignment.schedule_type}
                          </span>
                        </div>
                      )}
                      {assignment.time_windows && assignment.time_windows.length > 0 && (
                        <div className='flex flex-wrap gap-1 mt-1'>
                          {assignment.time_windows.map((window) => (
                            <span
                              key={window.id}
                              className='inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold'
                            >
                              {getParentName(window.assigned_user_id)}{' '}
                              {formatTimeDisplay(window.start_time)}–{formatTimeDisplay(window.end_time)}
                              {window.spans_next_day ? ' +1d' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Transition time labels */}
                      {(pickupLabel || dropoffLabel) && (
                        <div className='mt-2 space-y-1'>
                          {pickupLabel && (
                            <div className='flex items-center gap-1'>
                              <FiClock className='w-3 h-3 text-gray-500' />
                              <span className='text-xs text-gray-600'>{pickupLabel}</span>
                            </div>
                          )}
                          {dropoffLabel && (
                            <div className='flex items-center gap-1'>
                              <FiClock className='w-3 h-3 text-gray-500' />
                              <span className='text-xs text-gray-600'>{dropoffLabel}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className='text-right'>
                      <p
                        className='font-semibold'
                        style={{
                          color: isCurrentUserCustody ? '#008A9C' : '#6B7280',
                        }}
                      >
                        {getParentName(actualCustodyHolder)}
                      </p>
                      {assignment.is_exception && assignment.exception_status === 'PENDING' && (
                        <span
                          className='inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white'
                          style={{ backgroundColor: CUSTODY_COLORS.exception }}
                        >
                          {assignment.exception_action === 'BLOCK_CANCEL'
                            ? 'Pending block cancel'
                            : assignment.exception_action === 'MIXED'
                              ? 'Time change + cancel'
                              : assignment.exception_type === 'TIME_CHANGE'
                                ? 'Pending time change'
                                : 'Pending swap'}
                        </span>
                      )}
                      {assignment.is_exception && assignment.exception_status !== 'PENDING' && (
                        <span className='inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-primary'>
                          {assignment.exception_action === 'BLOCK_CANCEL'
                            ? 'Block canceled'
                            : assignment.exception_action === 'MIXED'
                              ? 'Time change + cancel'
                              : assignment.exception_type === 'TIME_CHANGE'
                                ? 'Time change'
                                : 'Swap'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedDayEvents.length > 0 && (
              <div className='mt-4 pt-4 border-t border-gray-200'>
                <h4 className='text-sm font-bold text-gray-900 mb-2'>Events</h4>
                <div className='space-y-2'>
                  {selectedDayEvents.map((event) => {
                    const childNames = (firstHousehold?.beneficiaries ?? [])
                      .filter((beneficiary) =>
                        event.beneficiary_ids.includes(beneficiary.beneficiary_id)
                      )
                      .map((beneficiary) => beneficiary.name)
                      .join(', ');
                    const responsibleName =
                      firstHousehold?.members.find(
                        (member) => member.user_id === event.responsible_user_id
                      )?.first_name ?? 'Both';

                    return (
                      <Link
                        key={`${event.event_id}-${event.original_start_at}`}
                        to='/app/calendar/events/$eventId'
                        params={{ eventId: String(event.event_id) }}
                        search={{ original_start_at: event.original_start_at }}
                        className='block rounded-xl border border-gray-200 bg-gray-50 p-3 hover:border-primary/30 transition-colors'
                      >
                        <p className='font-semibold text-gray-900'>{event.title}</p>
                        <p className='text-xs text-gray-600'>
                          {event.is_all_day
                            ? 'All day'
                            : new Date(event.start_at).toLocaleTimeString([], {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                          {event.location ? ` • ${event.location}` : ''}
                        </p>
                        {childNames ? (
                          <p className='text-xs text-gray-600'>Children: {childNames}</p>
                        ) : null}
                        <p className='text-xs font-semibold text-primary mt-1'>
                          Responsible: {responsibleName}
                        </p>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {currentUser &&
              selectedDayAssignments.map((assignment) => {
                const trans = getExchangeTransitionMeta(
                  assignment,
                  selectedDate,
                  calendarData,
                  currentUser.id,
                  coparent?.user_id
                );
                if (!trans) return null;
                const key = `${selectedDate}|${trans.exchangeType}`;
                const opposite = trans.exchangeType === 'PICKUP' ? 'DROPOFF' : 'PICKUP';
                const log =
                  exchangeLogsByKey.get(key) ??
                  exchangeLogsByKey.get(`${selectedDate}|${opposite}`);
                return (
                  <Link
                    key={`ex-${assignment.beneficiary_id}`}
                    to='/app/calendar/log-exchange'
                    search={{
                      beneficiary_id: String(assignment.beneficiary_id),
                      beneficiary_name: assignment.beneficiary_name,
                      scheduled_date: selectedDate,
                      scheduled_time: trans.scheduledTime,
                      exchange_type: trans.exchangeType,
                      co_parent_name: coparent?.first_name ?? 'Co-parent',
                    }}
                    onClick={() =>
                      track(log ? 'Exchange Log Viewed' : 'Log Exchange CTA Tapped', {
                        exchange_type: trans.exchangeType,
                        date: selectedDate,
                      })
                    }
                    className={`flex items-center justify-between w-full mt-2 px-4 py-3 rounded-xl font-semibold ${
                      log ? 'bg-gray-50 border border-gray-200' : 'bg-primary text-white'
                    }`}
                  >
                    {log ? (
                      <>
                        <ExchangeStatusBadge
                          status={log.status}
                          minutesLate={log.minutes_late}
                        />
                        <span className='text-gray-400 text-lg'>›</span>
                      </>
                    ) : (
                      <span>Log exchange · {assignment.beneficiary_name}</span>
                    )}
                  </Link>
                );
              })}
          </Card>
        )}

        {/* Empty State */}
        {!hasSchedules && !calendarQuery.isLoading && (
          <Card className='text-center py-8'>
            <FiCalendar className='w-12 h-12 text-gray-300 mx-auto mb-4' />
            <h3
              className='font-bold text-gray-900 mb-2'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              No Custody Schedules
            </h3>
            <p className='text-gray-500 mb-4'>
              Add a custody schedule to start tracking parenting time.
            </p>
            <Link to='/app/calendar/add-schedule'>
              <Button>Add Custody Schedule</Button>
            </Link>
          </Card>
        )}

        {/* Loading State */}
        {calendarQuery.isLoading && (
          <div className='flex justify-center py-8'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
          </div>
        )}
      </div>

      {/* FAB */}
      {hasSchedules && (
        <div className='fixed bottom-20 lg:bottom-6 right-6 z-40'>
          {fabOpen && (
            <div className='absolute bottom-16 right-0 flex flex-col gap-2 items-end'>
              <Link
                to='/app/calendar/events/add'
                search={{ date: selectedDate ?? undefined }}
                onClick={() => setFabOpen(false)}
                className='bg-white shadow-lg rounded-full px-4 py-3 font-semibold text-primary hover:bg-gray-50 transition-colors whitespace-nowrap'
              >
                Add Event
              </Link>
              <Link
                to='/app/calendar/add-check-in'
                onClick={() => {
                  track('Add Location Check In Clicked');
                  setFabOpen(false);
                }}
                className='bg-white shadow-lg rounded-full px-4 py-3 font-semibold text-primary hover:bg-gray-50 transition-colors whitespace-nowrap'
              >
                Add location check-in
              </Link>
              <Link
                to='/app/calendar/add-exception'
                search={{ date: selectedDate ?? undefined }}
                onClick={() => setFabOpen(false)}
                className='bg-white shadow-lg rounded-full px-4 py-3 font-semibold text-primary hover:bg-gray-50 transition-colors whitespace-nowrap'
              >
                Add Exception
              </Link>
              <Link
                to='/app/calendar/add-schedule'
                onClick={() => setFabOpen(false)}
                className='bg-white shadow-lg rounded-full px-4 py-3 font-semibold text-primary hover:bg-gray-50 transition-colors whitespace-nowrap'
              >
                Add Custody Schedule
              </Link>
            </div>
          )}
          <button
            onClick={() => setFabOpen(!fabOpen)}
            className='w-14 h-14 bg-primary rounded-full shadow-lg flex items-center justify-center hover:bg-primary-hover transition-colors'
          >
            <FiPlus className={`w-6 h-6 text-white transition-transform ${fabOpen ? 'rotate-45' : ''}`} />
          </button>
        </div>
      )}

      {/* FAB Overlay */}
      {fabOpen && (
        <div className='fixed inset-0 z-30' onClick={() => setFabOpen(false)} />
      )}
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/')({
  component: CalendarPage,
});
